import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, createFileRoute } from "@tanstack/react-router";
import { queryOptions } from "@tanstack/react-query";
import { useQuery } from "../../../lib/server-query";
import { queryPolicies, retainedQueryData } from "@sx/api-client";
import { Icon } from "@sx/ui";
import { toast } from "sonner";
import { Button, FullscreenButton } from "@sx/ui";

import { registerWorkspaceHeader } from "../../../lib/nav";
import { usePrincipalId } from "../../../lib/browser-state";
import { labelerQueryKeys } from "../../../lib/query-keys";
import { useLabelerQueryKey } from "../../../workspaces/label/query-keys";
import { useProjectId, useProject } from "../../../shell/project";

import { ConfirmationDialog } from "../../../components/ActionDialogs";
import { WorkspaceGate } from "../../../shell/WorkspaceGate";
import { ConnectivityIndicator } from "../../../workspaces/label/components/ConnectivityIndicator";
import { OfflineBanner } from "../../../workspaces/label/components/OfflineBanner";
import { labelerApi } from "../../../workspaces/label/api";
import { useLabelerApi } from "../../../workspaces/label/use-api";
import { translate, useLang } from "../../../workspaces/label/i18n";
import { labelNavItems } from "../../../workspaces/label/nav";
import {
  LabelLocalStateProvider,
  useLabelLocalState,
} from "../../../workspaces/label/local-state";
import {
  downloadOutboxRecovery,
  flush,
  useOutbox,
} from "../../../workspaces/label/offline-outbox";
import { SeatProvider } from "../../../workspaces/label/seat";
import { labelAccessQuery } from "../../../workspaces/label/access";
import type { CurrentUser } from "../../../workspaces/label/types";

/* Label's seat and access, named once so the loader and the layout ask for the same thing.
 *
 * Both are keyed by the platform principal, which is all the keying they need: staff
 * acting as an annotator hold that annotator's own session, so the labeler door answers
 * with that annotator's seat and the key is already theirs. */
const labelSeatQuery = (principalId: string, projectId: string) =>
  queryOptions({
    queryKey: labelerQueryKeys.principal(
      principalId,
      "project",
      projectId,
      "me",
    ),
    queryFn: labelerApi(projectId).me,
    ...queryPolicies.reference,
  });

export const Route = createFileRoute("/_console/label")({
  component: LabelLayout,
});

/* Everything the Label workspace needs established before a screen renders, and
 * nothing the console already establishes above it.
 *
 * The shell settled identity (one origin, one session). What is settled here is
 * the SEAT: the labeler door maps the platform principal to its own row and
 * answers with the role/username/id the whole workspace keys off. No labeler
 * grant is a 403 — a denial about this workspace, not a sign-out — which is
 * exactly what `WorkspaceGate` renders.
 *
 * Also session-long and idempotent: the offline outbox replayer, which drains
 * saves queued on a dropped rural link. It only observes the connectivity probe
 * the shell starts, so there is no init-order cycle. */
function LabelLayout() {
  const project = useProject();
  return project.selectedId === undefined ? (
    <div className="p-6">Select a project to open Labeling.</div>
  ) : (
    <ProjectLabelLayout key={project.selectedId} />
  );
}

function ProjectLabelLayout() {
  const projectId = useProjectId();
  const principalId = usePrincipalId();
  const seat = useQuery(labelSeatQuery(principalId, projectId));
  const access = useQuery(labelAccessQuery(principalId, projectId));
  const lang = useLang((s) => s.lang);

  // Both reads go through `retainedQueryData`, never `.data`: react-query keeps the last
  // success when a refetch fails, so a seat restored from the last reload would outlive
  // its own revocation and `WorkspaceGate`'s 403 branch would never be reached. A
  // permanent refusal drops the snapshot; a labeler door that is briefly unreachable —
  // the normal weather on a rural link — keeps the annotator working.
  const currentSeat = retainedQueryData(seat);
  const currentAccess = retainedQueryData(access);
  const role = currentSeat?.role;
  // Labels resolve against the persisted language here, because the shell
  // renders them verbatim — this is what keeps the nav in Hindi.
  const items = useMemo(
    () => (role && currentAccess ? labelNavItems(role, currentAccess) : null),
    [role, currentAccess, lang],
  );
  useEffect(() => {
    if (!items || role === undefined) return;
    return registerWorkspaceHeader({
      workspace: "experience-label",
      items,
      // Workbench, Audit, Principal and Admin are views of the one Labelling place: a tab
      // strip under the top bar, never rows in the rail.
      presentation: "tabs",
      status: <ConnectivityIndicator />,
      // The floor's one device control: annotation screens run full-screen for a whole
      // shift and Shift+F is muscle memory. It rides the workspace's header seam rather
      // than living in the shell because its tooltip is translated, and this is the one
      // workspace that speaks Hindi.
      tools: (
        <>
          <FullscreenButton
            enterLabel={translate(
              "fullscreen.enter",
              "Full screen (F11-style, Shift+F)",
            )}
            exitLabel={translate(
              "fullscreen.exit",
              "Exit full screen (Shift+F)",
            )}
          />
        </>
      ),
      badges: [{ label: role, variant: "secondary" }],
    });
    // `lang` is a dependency of the two translated strings above, not of `items` alone:
    // `translate` reads the persisted choice at call time, so the language IS the input.
  }, [items, role, lang]);

  // Said before the seat is known too: a floor that lost its link is still the labeler.
  if (!currentSeat)
    return (
      <>
        <OfflineBanner />
        <WorkspaceGate workspace="experience-label" query={seat} />
      </>
    );

  return (
    <SeatProvider seat={currentSeat}>
      <LabelLocalStateProvider principalId={principalId}>
        <OfflineBanner />
        <LegacyRecoveryBanner />
        <OutboxRecoveryBanner />
        <LlmCreditsBanner user={currentSeat} />
        <Outlet />
      </LabelLocalStateProvider>
    </SeatProvider>
  );
}

function OutboxRecoveryBanner() {
  const principalId = usePrincipalId();
  const terminal = useOutbox((state) => state.terminal);
  const [discardOpen, setDiscardOpen] = useState(false);
  if (terminal.length === 0) return null;
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center justify-center gap-3 border-b border-destructive/30 bg-destructive/10 px-4 py-2 text-supporting"
    >
      <span>
        {terminal.length} local Label write{terminal.length === 1 ? "" : "s"}{" "}
        need recovery and will not retry automatically.
      </span>
      <Button size="sm" onClick={() => downloadOutboxRecovery(principalId)}>
        Download recovery
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          terminal.forEach((op) =>
            useOutbox.getState().retryTerminal(op.idempotencyKey),
          );
          void flush();
        }}
      >
        Retry failed copies
      </Button>
      {terminal[0] ? (
        <Button size="sm" variant="outline" asChild>
          <Link
            to="/label/annotate/$episodeId"
            params={{ episodeId: terminal[0].episodeId }}
            search={terminal[0].requirementId
              ? { requirementId: terminal[0].requirementId }
              : { legacy: true, rev: "draft", tab: "subtasks" }}
          >
            Edit first episode
          </Link>
        </Button>
      ) : null}
      <Button size="sm" variant="outline" onClick={() => setDiscardOpen(true)}>
        Discard failed copies
      </Button>
      <ConfirmationDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="Discard failed local writes?"
        description="These recovery copies exhausted their automatic retries."
        detail={`Discarding removes all ${terminal.length} failed local ${terminal.length === 1 ? "write" : "writes"} from this device. This cannot be undone.`}
        confirmLabel="Discard failed copies"
        destructive
        onConfirm={() => {
          setDiscardOpen(false);
          terminal.forEach((op) =>
            useOutbox.getState().discardTerminal(op.idempotencyKey),
          );
        }}
      />
    </div>
  );
}

function LegacyRecoveryBanner() {
  const { legacyCount, legacyEpisodes, claimLegacy, discardLegacy } =
    useLabelLocalState();
  const [discardOpen, setDiscardOpen] = useState(false);
  if (legacyCount === 0) return null;
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center justify-center gap-3 border-b border-warning/30 bg-warning/10 px-4 py-2 text-supporting"
    >
      <span>
        {legacyCount} unowned item{legacyCount === 1 ? "" : "s"} across{" "}
        {legacyEpisodes.length} episode{legacyEpisodes.length === 1 ? "" : "s"}{" "}
        from the previous browser-storage format are quarantined and will
        not sync.
      </span>
      <Button
        size="sm"
        onClick={() =>
          void claimLegacy().catch(() =>
            toast.error(
              "Could not claim local work because browser storage is unavailable.",
            ),
          )
        }
      >
        Claim for this account
      </Button>
      <Button size="sm" variant="outline" onClick={() => setDiscardOpen(true)}>
        Discard
      </Button>
      <ConfirmationDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="Discard quarantined local work?"
        description="This work came from the previous browser-storage format and has no account owner."
        detail={`Discarding removes ${legacyCount} quarantined ${legacyCount === 1 ? "item" : "items"} from this device. This cannot be undone.`}
        confirmLabel="Discard local work"
        destructive
        onConfirm={() => {
          setDiscardOpen(false);
          discardLegacy();
        }}
      />
    </div>
  );
}

// Managers see the shared OpenRouter balance before an overdraw turns every
// AI-assisted label action into a mid-shift failure.
function LlmCreditsBanner({ user }: { user: CurrentUser }) {
  const api = useLabelerApi();
  const key = useLabelerQueryKey();
  const credits = useQuery({
    queryKey: key("llm-credits"),
    queryFn: api.llmCredits,
    enabled: user.role === "manager",
    refetchInterval: 10 * 60_000,
    ...queryPolicies.operational,
    retry: false,
  });
  const balance = credits.data;
  if (!balance?.low) return null;

  return (
    <div className="flex items-center gap-3 border-b bg-warning/15 px-5 py-2 text-supporting">
      <Icon name="warning" className="h-4 w-4 shrink-0 text-warning" />
      <span className="font-semibold">
        {translate(
          "credits.low",
          "OpenRouter credits are almost gone: ${n} left.",
        ).replace("{n}", balance.remaining_usd.toFixed(2))}{" "}
        {translate(
          "credits.consequence",
          "Generation, voice and AI fixes stop when it hits zero — top up now.",
        )}
      </span>
      <Button asChild size="sm" className="ml-auto shrink-0">
        <a
          href="https://openrouter.ai/settings/credits"
          target="_blank"
          rel="noreferrer"
        >
          {translate("credits.topup", "Top up")}
        </a>
      </Button>
    </div>
  );
}
